import React, { useState, useEffect, useCallback, useRef } from "react";
import { EnvironmentalEffects } from "@/components/environment/environmental-effects";
import { PokemonSprite } from "@/components/pokemon/pokemon-sprite";
import { PokemonPortrait } from "@/components/pokemon/pokemon-portrait";
import { StrokedText } from "@/components/ui/text/stroked-text";
import { Direction } from "types/battle-engine/battle";
import { typewriterEffect } from "@/lib/cutscene/CutsceneEngine";

interface RPGConversationLayoutProps {
  children?: React.ReactNode;
  speaker?: string;
  speakerPortrait?: string;
  text?: string;
  onAdvance?: () => void;
  onBack?: () => void;
}

export const RPGConversationLayout: React.FC<RPGConversationLayoutProps> = ({
  children,
  speaker = "Celebi",
  speakerPortrait = "celebi",
  text,
  onAdvance,
  onBack,
}) => {
  const [displayedText, setDisplayedText] = useState("");
  const [isTyping, setIsTyping] = useState(false);
  const abortControllerRef = useRef<AbortController | null>(null);

  const startTyping = useCallback(async (textToType: string) => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    abortControllerRef.current = new AbortController();

    setIsTyping(true);
    setDisplayedText("");

    try {
      await typewriterEffect(
        textToType,
        30,
        (updatedText) => {
          setDisplayedText(updatedText);
        },
        abortControllerRef.current.signal,
      );
    } catch (e) {
      // Ignore abort errors
    } finally {
      setIsTyping(false);
    }
  }, []);

  useEffect(() => {
    if (text) {
      startTyping(text);
    }
  }, [text, startTyping]);

  const handleAdvance = () => {
    if (isTyping && text) {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
      setDisplayedText(text);
      setIsTyping(false);
      return;
    }

    if (onAdvance) {
      onAdvance();
    }
  };

  return (
    <div
      className="relative w-full h-full overflow-hidden cursor-pointer"
      onClick={handleAdvance}
      style={{
        backgroundImage: `url("/assets/onboarding/intro-bg1.jpg")`,
        backgroundSize: "cover",
        backgroundPosition: "center",
      }}
    >
      {/* Cinematic Bars */}
      <div className="absolute w-full h-14 top-0 left-0 bg-black z-[9999]"></div>
      <div className="absolute w-full h-14 bottom-0 left-0 bg-black z-[9999]"></div>

      <EnvironmentalEffects weather="clouds" />

      {onBack && (
        <button
          onClick={(e) => {
            e.stopPropagation();
            onBack();
          }}
          className="top-6 left-4 z-[10000] absolute flex items-center gap-1 bg-black/50 backdrop-blur-sm px-3 py-2 border border-white/20 rounded-full text-white text-xs uppercase tracking-widest"
        >
          ← Back
        </button>
      )}

      {/* Celebi Sprite */}
      <div
        className="absolute transition-opacity duration-1000"
        style={{
          left: `50%`,
          top: `50%`,
          transform: "translate(-50%, -50%)",
        }}
      >
        <PokemonSprite
          pokemon={{
            base_pokemon_name: "celebi",
          }}
          direction={Direction.S}
          style={{
            scale: 3,
          }}
          showShadow
        />
      </div>

      {/* RPG Chat Box */}
      <div className="absolute w-[90%] max-w-[500px] bottom-[8%] left-1/2 -translate-x-1/2 z-[10000]">
        <div
          className="relative border-4 border-pk-beige-4/50 rounded-lg bg-pk-purple-1 p-1
          before:content-[''] before:absolute before:inset-[-10px] before:border-2 before:border-pk-beige-4/30 before:rounded-xl before:pointer-events-none
        "
        >
          <div className="bg-pk-purple-2 border-2 border-white/20 rounded-md p-4 flex flex-col gap-2">
            {/* Speaker Header */}
            <div className="flex items-center gap-3 -mt-10 mb-2">
              <div className="bg-pk-purple-1 p-1 border-2 border-white rounded-xl shadow-lg">
                <PokemonPortrait
                  pokemon={{
                    base_pokemon_name: speakerPortrait,
                  }}
                  emotion="Normal"
                  className="w-16 h-16"
                />
              </div>
              <div className="bg-pk-purple-1 px-4 py-1 border-2 border-white rounded-full shadow-md">
                <StrokedText
                  fontSize={20}
                  strokeWidth={6}
                  className="text-pk-yellow-1"
                >
                  {speaker}
                </StrokedText>
              </div>
            </div>

            {/* Dialog Text/Content */}
            <div className="min-h-[80px]">
              {text && (
                <p className="text-white text-sm font-medium leading-relaxed mb-4">
                  {displayedText}
                  {isTyping && (
                    <span className="inline-block w-2 h-5 bg-white ml-1 animate-pulse" />
                  )}
                </p>
              )}
              {children}
            </div>

            {/* Advance Indicator */}
            {!isTyping && !children && (
              <div className="flex justify-end mt-1">
                <div className="animate-bounce text-pk-yellow-1 text-xl">▼</div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
